
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/shared/Header";
import { AdvancedSearchFilters } from "@/components/search/AdvancedSearchFilters";
import { SearchResults } from "@/components/search/SearchResults";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Search, X, Filter } from "lucide-react";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [inputValue, setInputValue] = useState(searchParams.get('q') || '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    const query = searchParams.get('q') || '';
    setSearchQuery(query);
    setInputValue(query);
  }, [searchParams]);

  const handleSearch = () => {
    setSearchQuery(inputValue);
    if (inputValue) {
      setSearchParams({ q: inputValue });
    } else {
      setSearchParams({});
    }
  };

  const handleClearSearch = () => {
    setInputValue('');
    setSearchQuery('');
    setSearchParams({});
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const hasActiveFilters = Object.values(filters).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
    return Boolean(value);
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Search Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-6">Search Knowledge</h1>
            
            {/* Search Bar */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black" />
                    <Input
                      placeholder="Search across all knowledge domains..."
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="pl-10 pr-10"
                    />
                    {inputValue && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearSearch}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Button onClick={handleSearch}>
                    Search
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className={hasActiveFilters ? 'border-primary' : ''}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                    {hasActiveFilters && (
                      <span className="ml-2 bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center">
                        !
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Search Tips */}
            <div className="text-sm text-black mb-4">
              <span className="font-medium">Search tips:</span> Use quotes for exact phrases, 
              add + before words that must be included, or - to exclude words.
            </div>
          </div>

          {/* Search Interface */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <div className={`lg:col-span-1 ${showAdvancedFilters ? 'block' : 'hidden lg:block'}`}>
              <AdvancedSearchFilters 
                filters={filters} 
                onFiltersChange={setFilters} 
              />
            </div>

            {/* Search Results */}
            <div className={`${showAdvancedFilters ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              <SearchResults 
                searchQuery={searchQuery} 
                filters={filters} 
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
